// Quantidade pedida como ela sai nos PDFs do Compras: "36 UN (3 CX)" (pedido do Ruan, 28/10/2026).
//
// A quantidade do item da cotacao e contada em EMBALAGENS — a coluna "Tot Emb" da tela (regra dele,
// 29/10/2026: "sempre caixas"). O PDF imprime o conteudo (Tot Emb x Qtd Emb) em negrito e as caixas
// entre parenteses. A primeira versao (28/10) lia o numero como conteudo e a COT-2026-00076 saiu
// "3 UN (0,25 CX)" pra 3 caixas de disjuntor.
//
// So produto com a Qtd na Emb preenchida muda: sem ela nao ha como saber o conteudo da embalagem, e
// o texto segue com a unidade que o PDF ja mostrava (`unidadeSemEmbalagem`).
import { formatQtd } from './format/quantidade';

export interface EmbalagemDoProduto {
  /** Quantas unidades do conteudo cabem na embalagem: produtos.qtd_embalagem_padrao, ou a Qtd Emb
   *  da linha do fornecedor quando o documento e dele (pedido de compra). */
  qtdEmbalagemPadrao: number | string | null | undefined;
  /** A embalagem (CX, RL...): produtos.unidade_compra, ou a do fornecedor. */
  unidadeCompra: string | null | undefined;
  /** produtos.unidade_conteudo_embalagem (PR, MT...); vazia = a unidade de venda. */
  unidadeConteudo: string | null | undefined;
  unidadeVenda: string | null | undefined;
  /** O que o PDF mostrava antes, usado quando o produto nao tem Qtd na Emb. */
  unidadeSemEmbalagem: string | null | undefined;
}

export interface QuantidadeComEmbalagem {
  /** "36 UN" */
  principal: string;
  /** "(3 CX)", ou vazio quando nao ha embalagem diferente da unidade. */
  conversao: string;
}

function sigla(v: string | null | undefined): string {
  return (v ?? '').trim().toUpperCase();
}

// O numero chega do banco como "12.000" ou digitado na tela como "1,5". Nada de parseQtd: ele le
// "3.000" como tres mil (heuristica de milhar pt-BR).
function numero(v: number | string | null | undefined): number {
  return Number(String(v ?? '').replace(',', '.'));
}

/** Quantas unidades do conteudo cabem na embalagem; 1 quando nao se sabe. */
export function conteudoDaEmbalagem(qtdEmbalagem: number | string | null | undefined): number {
  const qe = numero(qtdEmbalagem);
  return Number.isFinite(qe) && qe > 0 ? qe : 1;
}

/** Quantas unidades tem a CAIXA em que a quantidade do item e contada: a do CADASTRO (regra do
 *  Ruan, 30/10/2026: "pegar o total de itens da caixa multiplicar pelo total de caixas"); a do
 *  fornecedor so quando o cadastro nao tem Qtd na Emb. Ate aqui valia a do fornecedor, e a
 *  Tramontina, que fatura por peca a tomada de caixa de 120, transformava 4 caixas em 4 tomadas
 *  (COT-2026-00079: Total Geral 9.522,20 contra 27.161,40 da soma das linhas). */
export function temCaixaNoCadastro(qtdEmbCadastro: number | string | null | undefined): boolean {
  const qc = numero(qtdEmbCadastro);
  return Number.isFinite(qc) && qc > 0;
}

export function conteudoDaCaixaDoItem(
  qtdEmbCadastro: number | string | null | undefined,
  qtdEmbFornecedor: number | string | null | undefined,
): number {
  return temCaixaNoCadastro(qtdEmbCadastro) ? numero(qtdEmbCadastro) : conteudoDaEmbalagem(qtdEmbFornecedor);
}

export function quantidadeComEmbalagem(
  embalagens: number | string | null | undefined,
  emb: EmbalagemDoProduto,
): QuantidadeComEmbalagem {
  const qtd = numero(embalagens);
  const qtdN = Number.isFinite(qtd) ? qtd : 0;
  const qe = numero(emb.qtdEmbalagemPadrao);
  if (!(Number.isFinite(qe) && qe > 0)) {
    return { principal: `${formatQtd(qtdN)} ${sigla(emb.unidadeSemEmbalagem) || 'UN'}`, conversao: '' };
  }
  const base = sigla(emb.unidadeConteudo) || sigla(emb.unidadeVenda) || 'UN';
  const compra = sigla(emb.unidadeCompra);
  // Sem nome de embalagem, ou embalagem com o mesmo nome do conteudo (13 + 9 cadastros ativos em
  // 29/10/2026), nao ha caixa pra converter: o numero sai como esta, na unidade do conteudo.
  if (qe === 1 || !compra || compra === base) return { principal: `${formatQtd(qtdN)} ${base}`, conversao: '' };
  return { principal: `${formatQtd(qtdN * qe)} ${base}`, conversao: `(${formatQtd(qtdN)} ${compra})` };
}
